import { FC, ReactNode, useMemo } from "react"
import classNames from "classnames"
import { STEPS_CONFIG } from "./config"
import { WearableModalType } from "./types"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import "./index.scss"

export interface WearableContentInfoProps {
   type: WearableModalType
   description?: ReactNode
}

const WearableContentInfo: FC<WearableContentInfoProps> = ({
   type,
   description,
}) => {
   const iconName: IconProps["name"] | null = useMemo(() => {
      switch (type) {
         case WearableModalType.SCAN:
            return "smartwatch"
         case WearableModalType.SCANNED:
            return "scanWearable"
         case WearableModalType.MANUAL_SWAP:
         case WearableModalType.MANUAL_ASSIGN:
            return "search"
         case WearableModalType.OPTIONS:
            return "moveInmate"
         case WearableModalType.ASSIGNED:
         case WearableModalType.SWAPPED:
         case WearableModalType.DISCONNECTED:
            return "safe"
         case WearableModalType.UNKNOWN:
         case WearableModalType.ALREADY_ASSIGNED:
            return "warning"
         default:
            return null
      }
   }, [type])

   const renderTitle = useMemo(() => {
      switch (type) {
         case WearableModalType.SCAN:
            return (
               <>
                  Assign <br /> ReassureBand
               </>
            )
         case WearableModalType.SCANNED:
            return (
               <>
                  ReassureBand <br /> scanned
               </>
            )
         case WearableModalType.MANUAL_ASSIGN:
            return (
               <>
                  <span className="wearable-content-info__title-prefix">
                     Manual
                  </span>
                  <br />
                  Assign ReassureBand
               </>
            )
         case WearableModalType.MANUAL_SWAP:
            return (
               <>
                  <span className="wearable-content-info__title-prefix">
                     Manual
                  </span>
                  <br />
                  Swap ReassureBand
               </>
            )
         case WearableModalType.OPTIONS:
            return (
               <>
                  <span className="wearable-content-info__title-prefix">
                     Option 1
                  </span>
                  <br />
                  Swap ReassureBand
               </>
            )
         case WearableModalType.ASSIGNED:
            return (
               <>
                  Assignment <br /> completed
               </>
            )
         case WearableModalType.SWAPPED:
            return (
               <>
                  Swap <br /> successful
               </>
            )
         case WearableModalType.DISCONNECTED:
            return (
               <>
                  Successful <br /> disconnection
               </>
            )
         case WearableModalType.UNKNOWN:
            return (
               <>
                  Unknown
                  <br />
                  ReassureBand
               </>
            )
         case WearableModalType.ALREADY_ASSIGNED:
            return (
               <>
                  ReassureBand already <br /> assigned
               </>
            )
         default:
            return null
      }
   }, [type])

   const steps = useMemo(() => STEPS_CONFIG[type] || [], [type])

   return (
      <div
         className={classNames("wearable-content-info", {
            [`wearable-content-info--${type.toLowerCase()}`]: [
               WearableModalType.OPTIONS,
               WearableModalType.MANUAL_ASSIGN,
               WearableModalType.MANUAL_SWAP,
               WearableModalType.SCAN,
               WearableModalType.SCANNED,
               WearableModalType.UNKNOWN,
               WearableModalType.ALREADY_ASSIGNED,
            ].includes(type),
         })}
      >
         <div className="wearable-content-info__header">
            {iconName && (
               <Icon
                  name={iconName}
                  size="52"
                  color={IconColor.WHITE}
               />
            )}
            {renderTitle && (
               <div className="wearable-content-info__title">{renderTitle}</div>
            )}
         </div>

         {steps && steps.length > 0 && (
            <div className="wearable-content-info__steps">
               {steps.map((step) => (
                  <div
                     key={step.label}
                     className="wearable-content-info__step"
                  >
                     <div className="wearable-content-info__step-label">
                        {step.label}
                     </div>
                     <span className="wearable-content-info__step-description">
                        {step.description}
                     </span>
                  </div>
               ))}
            </div>
         )}

         {description}
      </div>
   )
}

export default WearableContentInfo
